import { useEffect, useState } from "react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import { useAccounts, useSyncAccounts, useDisconnectAccount, useAvailableAccounts, useLinkAccount } from "@/hooks/use-accounts";
import { useAccountHealth, useRefreshAccountHealth } from "@/hooks/use-account-health";
import {
  useConnectZernio, useDisconnectZernio, useStartPlatformConnect,
  useZernioProfiles, useSetActiveProfile, useCreateProfile,
} from "@/hooks/use-zernio";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ConnectZernioCard } from "@/components/settings/ConnectZernioCard";
import { SocialAccountRow } from "@/components/settings/SocialAccountRow";
import { PlatformConnectGrid } from "@/components/settings/PlatformConnectGrid";
import { AvailableAccountRow } from "@/components/settings/AvailableAccountRow";
import { toast } from "sonner";

export function BrandSettingsAccountsPage() {
  const { t } = useTranslation("settings");
  const { brand, refetchBrand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const brandId = brand?.id ?? "";
  const zernioConnected = !!brand?.zernioConnected;

  const { data: accounts, isLoading } = useAccounts(brandId);
  const syncAll = useSyncAccounts(brandId);
  const disconnectAccount = useDisconnectAccount(brandId);
  const connectZernio = useConnectZernio(brandId);
  const disconnectZernio = useDisconnectZernio(brandId);
  const startConnect = useStartPlatformConnect(brandId);
  const profilesQuery = useZernioProfiles(brandId, zernioConnected);
  const setActiveProfile = useSetActiveProfile(brandId);
  const createProfile = useCreateProfile(brandId);
  const availableQuery = useAvailableAccounts(brandId);
  const linkAccount = useLinkAccount(brandId);
  const healthQuery = useAccountHealth(brandId);
  const refreshHealth = useRefreshAccountHealth(brandId);
  const healthByAccount = new Map(
    (healthQuery.data?.accounts ?? []).map((h) => [h.accountId, h]),
  );

  const [busyPlatform, setBusyPlatform] = useState<string | null>(null);
  const [newProfileName, setNewProfileName] = useState("");
  const [showNewProfile, setShowNewProfile] = useState(false);

  // The select shows the profile the user just picked while the PATCH and the
  // follow-up refetch are in flight, so the choice does not visibly revert.
  const [pendingProfileId, setPendingProfileId] = useState<string | null>(null);
  const profiles = profilesQuery.data?.profiles ?? [];
  const activeProfileId = profilesQuery.data?.activeProfileId ?? null;
  // A saved id missing from the list is stale — the profile was deleted in
  // Zernio, or the brand moved to a key that owns different profiles.
  const activeProfileIsKnown =
    !!activeProfileId && profiles.some((p) => p.id === activeProfileId);
  const staleProfile = !!activeProfileId && profiles.length > 0 && !activeProfileIsKnown;
  const selectedProfileId =
    pendingProfileId ?? (activeProfileIsKnown ? activeProfileId : "");

  useEffect(() => {
    if (pendingProfileId && activeProfileId === pendingProfileId) {
      setPendingProfileId(null);
    }
  }, [activeProfileId, pendingProfileId]);

  // Listen for the popup callback message
  useEffect(() => {
    function handle(ev: MessageEvent) {
      if (ev.origin !== window.location.origin) return;
      if (ev.data?.type === "zernio-connected") {
        syncAll.mutate();
        toast.success(t("accounts.accountConnected"));
        setBusyPlatform(null);
      }
    }
    window.addEventListener("message", handle);
    return () => window.removeEventListener("message", handle);
  }, [syncAll]);

  if (isLoading) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  async function handleStartConnect(platform: string) {
    setBusyPlatform(platform);
    try {
      const { authUrl } = await startConnect.mutateAsync(platform);
      const popup = window.open(authUrl, "zernio-oauth", "width=600,height=700");
      if (!popup) {
        window.location.href = authUrl;
      }
    } catch (e: any) {
      toast.error(e.message ?? t("accounts.failedStartConnect"));
      setBusyPlatform(null);
    }
  }

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("accounts.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("accounts.subtitle")}</p>
      </header>

      {/* Zernio connection — source-aware */}
      {brand?.zernioKeySource === "env" && (
        <section className="rounded-lg border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-sm font-medium">
              {t("accounts.zernioEnvConnected")}
            </span>
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground underline">
              {t("accounts.useBrandKey")}
            </summary>
            <div className="mt-3">
              <ConnectZernioCard
                connected={false}
                onConnect={(key) => connectZernio.mutateAsync(key).then(() => refetchBrand?.())}
                onDisconnect={async () => {}}
                disabled={!canManageBrand}
              />
            </div>
          </details>
        </section>
      )}

      {brand?.zernioKeySource === "brand" && (
        <section>
          <ConnectZernioCard
            connected={true}
            onConnect={(key) => connectZernio.mutateAsync(key).then(() => refetchBrand?.())}
            onDisconnect={() => disconnectZernio.mutateAsync().then(() => refetchBrand?.())}
            disabled={!canManageBrand}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            {t("accounts.customKeyNote")}
          </p>
        </section>
      )}

      {brand?.zernioKeySource === "none" && (
        <section>
          <ConnectZernioCard
            connected={false}
            onConnect={(key) => connectZernio.mutateAsync(key).then(() => refetchBrand?.())}
            onDisconnect={async () => {}}
            disabled={!canManageBrand}
          />
        </section>
      )}

      {zernioConnected && (
        <section className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t("accounts.activeProfile")}</h2>
            {canManageBrand && (
              <button
                type="button"
                className="text-xs underline"
                onClick={() => setShowNewProfile((v) => !v)}
              >
                {t("accounts.newProfile")}
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("accounts.activeProfileHelp")}
          </p>
          {profilesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("accounts.loadingProfiles")}</p>
          ) : profilesQuery.isError ? (
            <div className="flex items-center gap-3">
              <p className="text-sm text-destructive">{t("accounts.failedLoadProfiles")}</p>
              <Button size="sm" variant="outline" onClick={() => profilesQuery.refetch()}>
                {t("accounts.retryLoadProfiles")}
              </Button>
            </div>
          ) : (
            <>
              <select
                aria-label={t("accounts.activeProfile")}
                value={selectedProfileId}
                disabled={!canManageBrand || setActiveProfile.isPending}
                onChange={(e) => {
                  const profileId = e.target.value;
                  if (!profileId) return;
                  setPendingProfileId(profileId);
                  setActiveProfile.mutate(profileId, {
                    onSuccess: () => toast.success(t("accounts.profileUpdated")),
                    onError: (err) => {
                      setPendingProfileId(null);
                      toast.error(
                        err instanceof Error && err.message
                          ? err.message
                          : t("accounts.failedSetProfile"),
                      );
                    },
                  });
                }}
                className="block w-full max-w-md rounded border px-3 py-2 text-sm"
              >
                <option value="">{t("accounts.selectProfile")}</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {profiles.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("accounts.noProfiles")}</p>
              )}
              {staleProfile && !pendingProfileId && (
                <p className="text-xs text-destructive">{t("accounts.staleProfile")}</p>
              )}
            </>
          )}
          {showNewProfile && canManageBrand && (
            <div className="flex gap-2">
              <input
                aria-label={t("accounts.profileNamePlaceholder")}
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder={t("accounts.profileNamePlaceholder")}
                className="flex-1 rounded border px-3 py-2 text-sm"
              />
              <Button
                disabled={!newProfileName.trim() || createProfile.isPending}
                onClick={async () => {
                  const name = newProfileName.trim();
                  if (!name) return;
                  try {
                    await createProfile.mutateAsync({ name });
                    setNewProfileName("");
                    setShowNewProfile(false);
                    toast.success(t("accounts.profileCreated"));
                  } catch (err) {
                    toast.error(
                      err instanceof Error && err.message
                        ? err.message
                        : t("accounts.failedCreateProfile"),
                    );
                  }
                }}
              >
                {createProfile.isPending
                  ? t("accounts.creatingProfile")
                  : t("accounts.createProfile")}
              </Button>
            </div>
          )}
        </section>
      )}

      {zernioConnected && (
        <section className="rounded-lg border p-4 space-y-3">
          <h2 className="text-lg font-semibold">{t("accounts.connectPlatform")}</h2>
          <PlatformConnectGrid
            accounts={accounts ?? []}
            onConnect={handleStartConnect}
            busyPlatform={busyPlatform}
            disabled={!canManageBrand}
          />
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("accounts.linkedToBrand")}</h2>
          {zernioConnected && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  refreshHealth.mutate(undefined, {
                    onSuccess: () => toast.success(t("accounts.healthCheckStarted")),
                    onError: (err) => {
                      const msg = err instanceof Error ? err.message : "";
                      if (msg.includes("429") || /rate/i.test(msg)) {
                        toast.error(t("accounts.pleaseWaitRefresh"));
                      } else {
                        toast.error(t("accounts.failedHealthCheck"));
                      }
                    },
                  })
                }
                disabled={!canManageBrand || refreshHealth.isPending}
              >
                {refreshHealth.isPending ? t("accounts.checkingHealth") : t("accounts.refreshHealth")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncAll.mutate()}
                disabled={!canManageBrand || syncAll.isPending}
              >
                {syncAll.isPending ? t("accounts.syncing") : t("accounts.syncAll")}
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(accounts ?? []).map((a) => (
            <SocialAccountRow
              key={a.id}
              account={a}
              health={healthByAccount.get(a.id) ?? null}
              onSync={() => syncAll.mutate()}
              onDisconnect={() => {
                if (confirm(`Disconnect ${a.username ?? a.platform}?`)) {
                  disconnectAccount.mutate(a.id);
                }
              }}
              onReconnect={() => handleStartConnect(a.platform)}
              syncing={syncAll.isPending}
              canEdit={canManageBrand}
            />
          ))}
          {(accounts ?? []).length === 0 && (
            <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground col-span-full">
              {zernioConnected
                ? t("accounts.noAccountsZernio")
                : t("accounts.noAccountsConnect")}
            </div>
          )}
        </div>
      </section>

      {zernioConnected && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">{t("accounts.availableInZernio")}</h2>
          <p className="text-xs text-muted-foreground">
            {t("accounts.availableSubtitle")}
          </p>
          {availableQuery.isLoading ? (
            <div className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t("accounts.loadingAvailable")}
            </div>
          ) : (availableQuery.data?.available ?? []).length === 0 ? (
            <div className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t("accounts.noUnlinked")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(availableQuery.data?.available ?? []).map((a) => (
                <AvailableAccountRow
                  key={a.id}
                  account={a}
                  onLink={() => linkAccount.mutate(a.id)}
                  busy={linkAccount.isPending && linkAccount.variables === a.id}
                  disabled={!canManageBrand}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
